import { GifsResult } from '@giphy/js-fetch-api'
import { ReactNode, RefObject, useEffect, useState } from 'react'
import { Position, Range } from 'types'
import { ArtboardFilter, ArtboardRef } from '../artboard-gl/types'
import { MAX_PREVIEW_HEIGHT, MAX_PREVIEW_WIDTH } from './constants'
import { EditorProps, UGEContext } from './context'
import { useDuration } from './hooks/use-duration'
import { useMediaInfo } from './hooks/use-media-info'
import { useMediaSprite } from './hooks/use-media-sprite'
import { useMediaThumbnail } from './hooks/use-media-thumbnail'
import { usePreviewDimensions } from './hooks/use-preview-dimensions'
import { UGECropType, UGEEditor, UGEGAEventCallback, UGELayer, UGELayerProps } from './types'

type Props = {
    apiKey: string
    artboardRef: RefObject<ArtboardRef>
    children?: ReactNode
    crop?: Position
    defaultEditor?: UGEEditor
    enableStickers: boolean
    source: File | string
    fetchFavoriteStickers?: (offset: number) => Promise<GifsResult>
    setCrop: (crop?: Position) => void
    setGifIds: (ids: string[]) => void
    trackGAEvent: UGEGAEventCallback
}

const UGEContextManager = ({
    apiKey,
    artboardRef,
    children,
    crop,
    defaultEditor = 'default',
    enableStickers,
    source,
    fetchFavoriteStickers,
    setCrop,
    setGifIds,
    trackGAEvent,
}: Props) => {
    const [activeEditor, setActiveEditor] = useState<UGEEditor>(defaultEditor)
    const [activeEditorProps, setActiveEditorProps] = useState<EditorProps | undefined>(undefined)
    const [cropType, setCropType] = useState<UGECropType>('original')
    const [filter, setFilter] = useState<ArtboardFilter | undefined>()
    const [filterIntensity, setFilterIntensity] = useState<number | undefined>()
    const [layers, setLayers] = useState<UGELayer[]>([])
    const [paused, setPaused] = useState<boolean>(false)
    const [reversed, setReversed] = useState<boolean>(false)
    const [trim, setTrim] = useState<Range>({ start: 0, end: 1 })
    const sourceInfo = useMediaInfo(source)
    const sourceSprite = useMediaSprite(source, sourceInfo, MAX_PREVIEW_WIDTH)
    const duration = useDuration(sourceInfo?.duration || 0, layers, !!filter)
    const thumbnail = useMediaThumbnail(sourceSprite)

    const dimensions = usePreviewDimensions(
        sourceInfo?.width || MAX_PREVIEW_WIDTH,
        sourceInfo?.height || MAX_PREVIEW_HEIGHT
    )

    function addLayer(layer: UGELayer) {
        setLayers([...layers, layer])
    }

    function getProgress() {
        return artboardRef.current?.getProgress() || 0
    }

    function openEditor(editor: UGEEditor, props?: EditorProps) {
        setActiveEditorProps(props)
        setActiveEditor(editor)
    }

    function removeLayerById(id: string) {
        setLayers(layers.filter((layer) => layer.id !== id))
    }

    function resetEditorProps() {
        setActiveEditorProps(undefined)
    }

    function scrub(progress: number) {
        artboardRef.current?.setProgress(progress)
    }

    function updateLayerById(id: string, props: Partial<UGELayerProps>) {
        setLayers(
            layers.map((l) => {
                if (l.id === id) {
                    return { ...l, props: { ...l.props, ...props } }
                }

                return l
            })
        )
    }

    useEffect(() => {
        const layersWithGifIds = layers.filter((l) => !!l.gifId)
        const gifIds = layersWithGifIds.map((l) => l.gifId || '')

        setGifIds(gifIds)
    }, [layers])

    return (
        <UGEContext.Provider
            value={{
                activeEditor,
                activeEditorProps,
                apiKey,
                crop,
                cropType,
                dimensions,
                duration,
                enableStickers,
                filter,
                filterIntensity,
                layers,
                loaded: !!sourceInfo && !!sourceSprite,
                paused,
                reversed,
                source,
                sourceInfo,
                sourceSprite,
                thumbnail,
                trim,
                addLayer,
                fetchFavoriteStickers,
                getProgress,
                openEditor,
                removeLayerById,
                resetEditorProps,
                scrub,
                setCrop,
                setCropType,
                setFilter,
                setFilterIntensity,
                setLayers,
                setPaused,
                setReversed,
                setTrim,
                trackGAEvent,
                updateLayerById,
            }}
        >
            {children}
        </UGEContext.Provider>
    )
}

export default UGEContextManager
